/**
 * Cache Service
 * 
 * Provides request caching functionality based on patterns from the monolithic API service.
 * Supports TTL-based caching with different cache times for different data types.
 */
export class CacheService {
  private cache = new Map<string, { data: any; expires: number }>();
  private pendingRequests = new Map<string, Promise<any>>();
  private cleanupTimer: ReturnType<typeof setInterval>;

  constructor() {
    // Prune expired entries every 5 minutes to prevent unbounded memory growth
    this.cleanupTimer = setInterval(() => this.cleanupExpired(), 5 * 60 * 1000);
  }

  destroy(): void {
    clearInterval(this.cleanupTimer);
  }

  // Cache TTL settings (in milliseconds) - matches existing patterns
  private cacheTTL = {
    userProfile: 10 * 60 * 1000,      // 10 minutes
    userOrganizations: 5 * 60 * 1000, // 5 minutes
    userTeams: 5 * 60 * 1000,         // 5 minutes
    userRoles: 5 * 60 * 1000,         // 5 minutes
    userInvitations: 2 * 60 * 1000,   // 2 minutes
    organizationDetails: 5 * 60 * 1000, // 5 minutes
    organizationTeams: 3 * 60 * 1000,   // 3 minutes
    organizationMembers: 3 * 60 * 1000, // 3 minutes
    teamDetails: 3 * 60 * 1000,         // 3 minutes
    subscriptions: 5 * 60 * 1000,       // 5 minutes
    content: 2 * 60 * 1000,             // 2 minutes
    search: 1 * 60 * 1000,              // 1 minute
    permissions: 5 * 60 * 1000,         // 5 minutes
    roles: 10 * 60 * 1000,              // 10 minutes
    rbac: 5 * 60 * 1000,                // 5 minutes
    default: 3 * 60 * 1000              // 3 minutes default
  } as const;

  /**
   * Generate cache key from URL and parameters
   */
  private getCacheKey(url: string, params?: any): string {
    const paramString = params ? JSON.stringify(params) : '';
    return `${url}:${paramString}`;
  }

  /**
   * Determine cache type based on URL patterns (matches existing logic)
   */
  private getCacheType(url: string): keyof typeof this.cacheTTL {
    if (url.includes('/users/me') && !url.includes('/organizations') && !url.includes('/teams')) {
      return 'userProfile';
    }
    if (url.includes('/users/me/organizations')) return 'userOrganizations';
    if (url.includes('/users/me/teams')) return 'userTeams';
    if (url.includes('/users/me/roles')) return 'userRoles';
    if (url.includes('/users/me/invitations')) return 'userInvitations';
    if (url.includes('/organizations/') && url.includes('/teams')) return 'organizationTeams';
    if (url.includes('/organizations/') && url.includes('/members')) return 'organizationMembers';
    if (url.includes('/organizations/')) return 'organizationDetails';
    if (url.includes('/teams/')) return 'teamDetails';
    if (url.includes('/subscriptions')) return 'subscriptions';
    if (url.includes('/content')) return 'content';
    if (url.includes('/search')) return 'search';
    if (url.includes('/permissions')) return 'permissions';
    if (url.includes('/roles')) return 'roles';
    if (url.includes('/rbac')) return 'rbac';
    
    return 'default';
  }

  /**
   * Get data from cache if valid
   */
  get<T>(url: string, params?: any): T | null {
    const cacheKey = this.getCacheKey(url, params);
    const cached = this.cache.get(cacheKey);

    if (cached && cached.expires > Date.now()) {
      return cached.data as T;
    }

    if (cached) {
      this.cache.delete(cacheKey);
    }

    return null;
  }

  /**
   * Set data in cache with appropriate TTL
   */
  set<T>(url: string, data: T, params?: any): void {
    const cacheKey = this.getCacheKey(url, params);
    const cacheType = this.getCacheType(url);
    const ttl = this.cacheTTL[cacheType];
    const expires = Date.now() + ttl;
    
    this.cache.set(cacheKey, { data, expires });
  }

  /**
   * Check if there's a pending request for this key
   */
  getPendingRequest<T>(url: string, params?: any): Promise<T> | null {
    const cacheKey = this.getCacheKey(url, params);
    return this.pendingRequests.get(cacheKey) || null;
  }

  /**
   * Set a pending request
   */
  setPendingRequest<T>(url: string, promise: Promise<T>, params?: any): void {
    const cacheKey = this.getCacheKey(url, params);
    this.pendingRequests.set(cacheKey, promise);
    
    // Clean up when the promise resolves
    promise.finally(() => {
      this.pendingRequests.delete(cacheKey);
    });
  }

  /**
   * Clear all cache
   */
  clearCache(): void {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  /**
   * Invalidate user-related cache entries
   */
  invalidateUserCache(): void {
    const keysToDelete = Array.from(this.cache.keys()).filter(key => 
      key.includes('/users/me') || key.includes('/users/profile')
    );
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Invalidate organization-related cache entries
   */
  invalidateOrganizationCache(orgId?: string): void {
    const keysToDelete = Array.from(this.cache.keys()).filter(key => {
      if (orgId) {
        return key.includes(`/organizations/${orgId}`) || 
               key.includes('/users/me/organizations');
      }
      return key.includes('/organizations') || 
             key.includes('/users/me/organizations');
    });
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Invalidate team-related cache entries
   */
  invalidateTeamCache(teamId?: string, orgId?: string): void {
    const keysToDelete = Array.from(this.cache.keys()).filter(key => {
      if (teamId) {
        return key.includes(`/teams/${teamId}`) || 
               key.includes('/users/me/teams');
      }
      if (orgId) {
        return key.includes(`/organizations/${orgId}/teams`) || 
               key.includes('/users/me/teams');
      }
      return key.includes('/teams') || 
             key.includes('/users/me/teams');
    });
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Invalidate content-related cache entries
   */
  invalidateContentCache(): void {
    const keysToDelete = Array.from(this.cache.keys()).filter(key => 
      key.includes('/content')
    );
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Invalidate subscription-related cache entries
   */
  invalidateSubscriptionCache(): void {
    const keysToDelete = Array.from(this.cache.keys()).filter(key =>
      key.includes('/subscriptions')
    );
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Invalidate search-related cache entries
   */
  invalidateSearchCache(): void {
    const keysToDelete = Array.from(this.cache.keys()).filter(key => 
      key.includes('/search')
    );
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): {
    totalEntries: number;
    pendingRequests: number;
    expiredEntries: number;
    cacheTypes: Record<string, number>;
  } {
    const now = Date.now();
    const expiredEntries = Array.from(this.cache.values()).filter(entry => entry.expires <= now).length;
    
    // Count entries by cache type
    const cacheTypes: Record<string, number> = {};
    for (const key of this.cache.keys()) {
      const url = key.split(':')[0];
      const type = this.getCacheType(url);
      cacheTypes[type] = (cacheTypes[type] || 0) + 1;
    }

    return {
      totalEntries: this.cache.size,
      pendingRequests: this.pendingRequests.size,
      expiredEntries,
      cacheTypes
    };
  }

  /**
   * Clean up expired entries
   */
  cleanupExpired(): number {
    const now = Date.now();
    let cleanedCount = 0;
    
    for (const [key, entry] of this.cache.entries()) {
      if (entry.expires <= now) {
        this.cache.delete(key);
        cleanedCount++;
      }
    }
    
    return cleanedCount;
  }
}

// Export singleton instance
export const cacheService = new CacheService();